<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <h1 class="guests-page__title">
        <template v-if="facility">{{ facility.name }}</template>
        <template v-else>Dettaglio struttura</template>
      </h1>
      <div style="display: flex; gap: 10px; flex-wrap: wrap">
        <router-link to="/facilities" class="villa-btn villa-btn--ghost">
          <i class="fa-solid fa-arrow-left"></i> Strutture
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <template v-else-if="facility">
      <!-- Navigazione interna alla struttura (mini-gestionale, D6C-ARCH) -->
      <div class="facility-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="facility-tab"
          :class="{ 'facility-tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- ============ TAB: DASHBOARD STRUTTURA ============ -->
      <section v-show="activeTab === 'dashboard'">
        <!-- Anagrafica struttura -->
        <div class="guest-detail">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Tipologia</span>
              <span class="detail-item__value">{{
                typeLabel(facility.type)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Stato</span>
              <span class="detail-item__value">
                <span
                  :class="[
                    'facility-badge',
                    facility.isActive
                      ? 'facility-badge--active'
                      : 'facility-badge--inactive',
                  ]"
                >
                  {{ facility.isActive ? "Attiva" : "Non attiva" }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Indirizzo</span>
              <span class="detail-item__value">
                {{ facility.address || "—" }}
                <template v-if="facility.city">, {{ facility.city }}</template>
                <template v-if="facility.province">
                  ({{ facility.province }})</template
                >
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Retta media attesa</span>
              <span class="detail-item__value">
                {{ euro(facility.expectedMonthlyFee) }} / mese
              </span>
            </div>
          </div>
        </div>

        <h3
          class="guests-page__title occ-section-title"
          style="margin-top: 20px"
        >
          Stato operativo
        </h3>
        <div class="dash-insights">
          <div class="dash-card">
            <div class="dash-card__value">{{ facility.maxCapacity }}</div>
            <div class="dash-card__label">Capienza massima</div>
          </div>
          <div class="dash-card">
            <div class="dash-card__value">{{ occ.totalRooms }}</div>
            <div class="dash-card__label">Camere</div>
          </div>
          <!-- Card unica posti/ospiti: occupati / totali + info posti liberi e % -->
          <div class="dash-card">
            <div class="dash-card__value">
              {{ occ.occupiedBeds }} / {{ occ.totalBeds }}
            </div>
            <div class="dash-card__label">
              Ospiti (occupati su posti totali)
            </div>
            <div class="dash-card__hint">
              {{ occ.availableBeds }} posti liberi · {{ occ.occupancyRate }}%
              occupazione
            </div>
          </div>
        </div>

        <!-- Metriche economiche dinamiche (D6C-FIX) — solo dev/admin (D10A) -->
        <template v-if="canViewEconomics">
          <h3
            class="guests-page__title"
            style="font-size: 16px; margin: 22px 0 12px"
          >
            Metriche economiche
          </h3>
          <div class="dash-insights">
            <div class="dash-card">
              <div class="dash-card__value">
                {{ euro(eco.currentMonthlyRevenue) }}
              </div>
              <div class="dash-card__label">Rendita mensile attuale</div>
            </div>
            <div class="dash-card">
              <div class="dash-card__value">
                {{ euro(eco.potentialMonthlyRevenue) }}
              </div>
              <div class="dash-card__label">Rendita mensile potenziale</div>
            </div>
            <div class="dash-card">
              <div class="dash-card__value">{{ euro(eco.revenueGap) }}</div>
              <div class="dash-card__label">Gap potenziale</div>
            </div>
            <div class="dash-card">
              <div class="dash-card__value">
                {{ euro(eco.expectedMonthlyFee) }}
              </div>
              <div class="dash-card__label">Retta media attesa</div>
            </div>
            <div class="dash-card">
              <div class="dash-card__value">
                {{ euro(eco.averageMonthlyFee) }}
              </div>
              <div class="dash-card__label">Retta media reale</div>
            </div>
            <div class="dash-card">
              <div class="dash-card__value">{{ eco.payingGuestsCount }}</div>
              <div class="dash-card__label">Ospiti con retta valorizzata</div>
            </div>
          </div>
        </template>
      </section>

      <!-- ============ TAB: OSPITI ============ -->
      <FacilityGuestsPanel
        v-if="activeTab === 'guests'"
        :facility-id="facilityId"
        :facility-name="facility.name"
        @changed="reloadDashboard"
      />

      <!-- ============ TAB: CAMERE ============ -->
      <FacilityRoomsPanel
        v-if="activeTab === 'rooms'"
        :facility-id="facilityId"
        :facility-name="facility.name"
        @changed="reloadDashboard"
      />

      <!-- ============ TAB: OCCUPAZIONE ============ -->
      <FacilityOccupancyPanel
        v-if="activeTab === 'occupancy'"
        :facility-id="facilityId"
        @changed="reloadDashboard"
      />

      <!-- ============ TAB: TEMPLATE DOCUMENTALI ============ -->
      <FacilityDocumentTemplatesPanel
        v-if="activeTab === 'templates'"
        :facility-id="facilityId"
      />

      <!-- ============ TAB: DOCUMENTI RETTA (D10B, dev/admin) ============ -->
      <FacilityBillingPanel
        v-if="activeTab === 'billing'"
        :facility-id="facilityId"
      />
    </template>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FacilityGuestsPanel from "@/components/FacilityGuestsPanel.vue";
import FacilityRoomsPanel from "@/components/FacilityRoomsPanel.vue";
import FacilityOccupancyPanel from "@/components/FacilityOccupancyPanel.vue";
import FacilityDocumentTemplatesPanel from "@/components/FacilityDocumentTemplatesPanel.vue";
import FacilityBillingPanel from "@/components/FacilityBillingPanel.vue";

const TYPES = {
  residential_housing: "Alloggio residenziale",
  retirement_home: "Casa di riposo",
  protected_home: "Casa protetta",
  rsa: "RSA",
  community_housing: "Comunità alloggio",
  other: "Altro",
};

export default {
  name: "FacilityDetailPage",
  components: {
    FacilityGuestsPanel,
    FacilityRoomsPanel,
    FacilityOccupancyPanel,
    FacilityDocumentTemplatesPanel,
    FacilityBillingPanel,
  },
  props: {
    facilityId: { type: [String, Number], required: true },
  },
  data() {
    return {
      facility: null,
      loading: true,
      activeTab: "dashboard",
      guests: [],
      rooms: [],
      occ: {
        totalRooms: 0,
        totalBeds: 0,
        occupiedBeds: 0,
        availableBeds: 0,
        occupancyRate: 0,
        rooms: [],
      },
    };
  },
  computed: {
    ...mapGetters(["canViewEconomics", "isAdminUser"]),
    // Tab della struttura. "Documenti retta" (D10B) solo per dev/admin.
    tabs() {
      const base = [
        {
          key: "dashboard",
          label: "Dashboard struttura",
          icon: "fa-solid fa-gauge-high",
        },
        { key: "guests", label: "Ospiti", icon: "fa-solid fa-users" },
        { key: "rooms", label: "Camere", icon: "fa-solid fa-door-open" },
        {
          key: "occupancy",
          label: "Occupazione",
          icon: "fa-solid fa-chart-pie",
        },
        {
          key: "templates",
          label: "Template documentali",
          icon: "fa-solid fa-file-lines",
        },
      ];
      if (this.isAdminUser) {
        base.push({
          key: "billing",
          label: "Documenti retta",
          icon: "fa-solid fa-file-invoice",
        });
      }
      return base;
    },
    // Metriche economiche dinamiche, fornite dal backend dentro facility.
    eco() {
      return (this.facility && this.facility.economics) || {};
    },
  },
  methods: {
    ...mapActions([
      "fetchFacility",
      "fetchFacilityGuests",
      "fetchFacilityRooms",
      "fetchFacilityOccupancy",
    ]),
    typeLabel(v) {
      return TYPES[v] || v;
    },
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
    async load() {
      this.loading = true;
      try {
        this.facility = await this.fetchFacility(this.facilityId);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Struttura non trovata."
        );
        this.$router.push("/facilities");
        return;
      } finally {
        this.loading = false;
      }
      // Contatori della Dashboard struttura (non bloccanti).
      await this.reloadDashboard();
    },
    // Ricarica i soli contatori della Dashboard struttura, senza toccare lo
    // stato di loading (così non smonta il pannello attivo). Richiamato anche
    // quando un pannello operativo emette "changed".
    async reloadDashboard() {
      try {
        // Ricarico anche la struttura per rinfrescare le metriche economiche
        // (facility.economics) dopo modifiche a rette/ospiti.
        const [facility, guests, rooms, occ] = await Promise.all([
          this.fetchFacility(this.facilityId),
          this.fetchFacilityGuests(this.facilityId),
          this.fetchFacilityRooms(this.facilityId),
          this.fetchFacilityOccupancy(this.facilityId),
        ]);
        this.facility = facility;
        this.guests = guests;
        this.rooms = rooms;
        this.occ = occ;
      } catch {
        // non bloccante: in caso di errore i contatori restano invariati
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
